import { faCheck, faSpinner, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import { useEffect, useState } from "react";
import CustomSelect from "../CustomSelect";
import { chaseLogsData } from "../../utils/data/chase-logs-data";
import { toast } from "react-toastify";
import { ParseErrors } from "../../utils/ParseErrors";
import axiosInstance from "../../utils/axiosInstance";
import { useChaseMenu } from "../../store/use-chase-menu";
import downloadFile from "../../utils/downloadFile";

const AddNewLog = ({ onUpdateLogsRowType, UpdateItemData }) => {
  const { initialValues } = useChaseMenu();
  const [selectType, setSelectType] = useState<"select" | "free">("select");
  const [typeData, setTypeData] = useState(
    chaseLogsData.find((chaselog) => chaselog.value === "default").fields
  );
  const [values, setValues] = useState<Record<string, any>>({});
  const [loadings, setLoadings] = useState<boolean>(false);

  // ---------------------------------------------- ADD LOGS --------------------------------

  const HandleAddLog = async () => {
    if (validateLog()) {
      setLoadings(true);
      try {
        const res = await axiosInstance.post(
          `/loan/add/log/${initialValues?.id}`,
          values
        );
        if (res.data.file) {
          downloadFile(res.data.file);
        }
        onUpdateLogsRowType("none");
        UpdateItemData();
        setValues({});
        toast.success("Log added successfully!");
        setLoadings(false);
      } catch (error) {
        setLoadings(false);
        console.error(error);
        toast.error(ParseErrors(error));
      }
    }
  };

  const onChangeValue = (name: string, value: string) => {
    setValues({ ...values, [name]: value });
  };

  const validateLog = () => {
    for (let i = 0; i < typeData.length; i++) {
      let typedata = typeData[i];

      if (typedata.isRequired) {
        if (!values[typedata.name]) {
          toast.error(`Please enter ${typedata.placeHolder}`);
          return false;
        }
      }
    }

    if (values?.type === "refund-raised" && values?.collectedAmount >= 0) {
      toast.error("Amount Must be a negative value");
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (typeData === undefined) {
      setTypeData(
        chaseLogsData.find((chaselog) => chaselog.value === "default").fields
      );
    }
  }, [typeData]);

  /* Active fields, filtering out empty spacer entries */
  const activeFields = ((typeData as any[]) ?? []).filter((item) => !!item?.name);

  /**
   * Outstanding is derived, never typed.
   *
   * The previous expression was `Number(a - b)?.toFixed(2) || 0`, which renders
   * "NaN" whenever collectedAmount is still empty: `undefined` poisons the
   * subtraction, and `NaN.toFixed(2)` returns the *string* "NaN", which is truthy —
   * so the `|| 0` fallback could never fire.
   */
  const derivedOutstanding = () => {
    const balance = Number(initialValues.data?.outstandingBalance ?? 0);
    const collected = Number(values?.collectedAmount ?? 0);
    const remaining = balance - collected;
    return Number.isFinite(remaining) ? remaining.toFixed(2) : "0.00";
  };

  return (
    <div className="mt-3 rounded-xl border border-edge bg-surface-elevated p-4">
      <div className="flex items-center justify-between gap-4 mb-3">
        <p className="flex items-center gap-2 text-[11px] font-semibold text-light-gray uppercase tracking-[0.14em]">
          <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
          New Entry
        </p>

        {/* Same 28px rounded-lg icon buttons as the table row actions. */}
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={HandleAddLog}
            disabled={loadings}
            title="Save entry"
            className="cursor-pointer h-7 w-7 rounded-lg flex items-center justify-center text-emerald-600 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-500/15 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FontAwesomeIcon
              icon={loadings ? faSpinner : faCheck}
              spin={loadings}
              className="text-[15px]"
            />
          </button>
          {/* Discards an unsaved draft — a cross, not a bin. Nothing exists to delete yet. */}
          <button
            type="button"
            onClick={() => onUpdateLogsRowType("none")}
            disabled={loadings}
            title="Discard entry"
            className="cursor-pointer h-7 w-7 rounded-lg flex items-center justify-center text-light-gray hover:text-red-600 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/15 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FontAwesomeIcon icon={faXmark} className="text-[15px]" />
          </button>
        </div>
      </div>

      {/* ── Date · type ── mirrors the date/description columns of the list above. */}
      <div className="flex items-center gap-3 flex-wrap">
        <input
          type="text"
          disabled
          value={moment(new Date()).format("DD/MM/YYYY")}
          className="w-[100px] shrink-0 bg-input-bg border border-input-border text-light-gray rounded-lg text-[12px] font-semibold tabular-nums h-9 outline-none px-3 cursor-default"
        />

        <div className="flex-1 min-w-[200px] relative z-20">
          {selectType === "select" ? (
            <CustomSelect
              inputClass="!rounded-lg !h-9 !text-[13px]"
              comboboxType="select"
              valueProperty="value"
              value={chaseLogsData.find(
                (chaseDV) => chaseDV?.value === values?.type
              )}
              options={chaseLogsData}
              labelProperty="label"
              onSingleSelectChange={(e) => {
                onChangeValue("type", e?.value);
                setTypeData(
                  chaseLogsData.find((chaseDV) => chaseDV?.value === e?.value)
                    ?.fields
                );
                if (e?.value === "others") setSelectType("free");
              }}
            />
          ) : (
            <input
              type="text"
              placeholder="Describe this entry"
              className="w-full bg-input-bg border border-input-border focus:border-indigo-400/40 focus:ring-2 focus:ring-indigo-400/20 text-text-primary placeholder:text-light-gray rounded-lg text-[13px] h-9 outline-none px-3 transition-all"
              onChange={(e) => onChangeValue("type", e.target.value)}
            />
          )}
        </div>
      </div>

      {/* ── Dynamic fields ── */}
      {activeFields.length > 0 && (
        <div className="flex flex-wrap gap-3 mt-3">
          {activeFields.map((item) => {
            const isMoney = item?.type !== "string";
            const isDerived = item?.name === "outstandingBalance";

            return (
              <div key={item.name} className="flex-1 min-w-[170px]">
                <label className="block mb-1.5 text-[11px] font-medium text-light-gray">
                  {item?.placeHolder}
                  {isDerived ? (
                    <span className="ml-1 text-light-gray/70">(auto)</span>
                  ) : null}
                </label>

                {item?.type === "select" ? (
                  <CustomSelect
                    inputClass="!rounded-lg !h-9 !text-[13px]"
                    comboboxType="select"
                    placeholder={item?.placeHolder}
                    options={(item as any)?.options}
                    labelProperty="label"
                    valueProperty="value"
                    onSingleSelectChange={(e) =>
                      onChangeValue(item?.name, e.value)
                    }
                    value={(item as any)?.options?.find(
                      (option: any) => option?.value === values[item?.name]
                    )}
                  />
                ) : (
                  <div className="relative">
                    {/* £ sits inside the field rather than floating beside it, so the
                        symbol belongs to the input it labels. */}
                    {isMoney ? (
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] font-medium text-light-gray pointer-events-none">
                        £
                      </span>
                    ) : null}
                    <input
                      type="text"
                      value={
                        isDerived
                          ? derivedOutstanding()
                          : values[item?.name] ?? ""
                      }
                      placeholder={item?.placeHolder}
                      className={`w-full bg-input-bg border border-input-border focus:border-indigo-400/40 focus:ring-2 focus:ring-indigo-400/20 text-text-primary placeholder:text-light-gray rounded-lg text-[13px] tabular-nums h-9 outline-none pr-3 transition-all disabled:opacity-70 disabled:cursor-default ${
                        isMoney ? "pl-7" : "pl-3"
                      }`}
                      onChange={(e) => onChangeValue(item?.name, e.target.value)}
                      disabled={isDerived}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AddNewLog;
